/* v6.35.5 — Live diagnostic panel for the dedicated _LLM_EXECUTOR pool.
 *
 * Polls GET /api/bp-auditor/diagnostics/executor every 10s.
 * Shows:
 *   - Occupancy bar (active_runners / max_workers)
 *   - Oldest thread age — color chip (green ≤ 60s, amber 60-600s, red > 600s)
 *   - Per-thread ages list
 *   - Recent flush history (last 5)
 *   - Password-gated "Force Flush" button → POST /api/bp-auditor/diagnostics/executor/flush
 *
 * Why this exists: v6.35.3 added the self-heal but operators had no live
 * window into the pool. This panel makes saturation visible BEFORE a wedge
 * happens, and gives the operator a manual escape hatch for forensic recovery.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Activity, AlertTriangle, RefreshCw, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const ageChipColor = (sec) =>
  sec <= 60
    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : sec <= 600
    ? 'bg-amber-100 text-amber-800 border-amber-300'
    : 'bg-rose-100 text-rose-800 border-rose-300';

const fmtAge = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const BPExecutorDiagnosticsPanel = () => {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flushing, setFlushing] = useState(false);
  const [pw, setPw] = useState('');
  const [showFlushForm, setShowFlushForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/bp-auditor/diagnostics/executor`,
        authCfg(),
      );
      setDiag(data);
    } catch (e) {
      // Silent — panel is non-critical. The error toast on a sub-panel
      // would just add noise during a real wedge.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const doFlush = async () => {
    if (!pw) {
      toast.error('Password required');
      return;
    }
    setFlushing(true);
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/diagnostics/executor/flush`,
        { password: pw },
        authCfg(),
      );
      const d = data?.decision || {};
      toast.success(
        d.flushed
          ? `Pool flushed — abandoned ${d.stuck || 0}/${d.max_workers || 10} slots`
          : 'Flush call accepted but pool was idle — nothing to abandon.',
      );
      setPw('');
      setShowFlushForm(false);
      await load();
    } catch (e) {
      toast.error(`Flush failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFlushing(false);
    }
  };

  if (loading) {
    return (
      <div
        className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-500"
        data-testid="bp-exec-diag-loading"
      >
        Loading executor pool diagnostics…
      </div>
    );
  }

  const state = diag?.state || {};
  const history = diag?.flush_history || [];
  const max = state.max_workers || 10;
  const active = state.active_runners || 0;
  const oldest = state.oldest_active_age_sec || 0;
  const occupancyPct = Math.round((active / max) * 100);
  const occupancyColor =
    active === 0
      ? 'bg-emerald-500'
      : active < max * 0.5
      ? 'bg-blue-500'
      : active < max * 0.8
      ? 'bg-amber-500'
      : 'bg-rose-500';

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4 space-y-3"
      data-testid="bp-exec-diag-panel"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-[#002FA7]" />
          <h3 className="text-sm font-semibold">content-engine Executor Pool</h3>
          <Badge variant="outline" className="text-[10px]">v6.35.5</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            data-testid="bp-exec-diag-refresh-btn"
            className="h-7 text-xs"
          >
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFlushForm((v) => !v)}
            data-testid="bp-exec-diag-flush-toggle"
            className="h-7 text-xs"
          >
            <Zap size={12} className="mr-1" /> Force Flush
          </Button>
        </div>
      </div>

      {/* Occupancy bar */}
      <div data-testid="bp-exec-diag-occupancy">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-600">
            Occupancy{' '}
            <span className="font-semibold text-gray-900">
              {active} / {max}
            </span>{' '}
            <span className="text-gray-400">({occupancyPct}%)</span>
          </span>
          {active > 0 && (
            <span
              className={`text-[10px] px-1.5 py-0.5 border rounded ${ageChipColor(oldest)}`}
              data-testid="bp-exec-diag-oldest-chip"
              data-oldest-bucket={oldest <= 60 ? 'healthy' : oldest <= 600 ? 'slow' : 'zombie-likely'}
            >
              oldest: {fmtAge(oldest)}
            </span>
          )}
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${occupancyColor} transition-all duration-500`}
            style={{ width: `${occupancyPct}%` }}
          />
        </div>
      </div>

      {/* Per-thread ages */}
      {(state.ages_sec || []).length > 0 && (
        <div className="text-[11px] text-gray-600" data-testid="bp-exec-diag-ages">
          <span className="font-medium">Per-thread age:</span>{' '}
          {(state.ages_sec || []).slice(0, 10).map((sec, i) => (
            <span
              key={i}
              className={`inline-block px-1.5 py-0.5 mx-0.5 border rounded text-[10px] ${ageChipColor(sec)}`}
            >
              {fmtAge(sec)}
            </span>
          ))}
        </div>
      )}

      {active === 0 && (
        <div className="text-xs text-gray-500" data-testid="bp-exec-diag-idle">
          Pool is idle. No workers in flight.
        </div>
      )}

      {/* Flush form */}
      {showFlushForm && (
        <div
          className="bg-amber-50 border border-amber-200 rounded p-3 space-y-2"
          data-testid="bp-exec-diag-flush-form"
        >
          <div className="text-xs text-amber-900 flex items-start gap-1.5">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>
              Force flush will abandon all in-flight content-engine threads — they keep
              running on their dead sockets but stop competing for new audit
              work. Safe when the pool shows zombies; disruptive if any thread
              is legitimately mid-call. Password required.
            </span>
          </div>
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder="Super-admin password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="text-xs h-8"
              data-testid="bp-exec-diag-flush-password"
            />
            <Button
              size="sm"
              onClick={doFlush}
              disabled={flushing || !pw}
              data-testid="bp-exec-diag-flush-submit"
              className="h-8 text-xs"
            >
              {flushing ? 'Flushing…' : 'Confirm Flush'}
            </Button>
          </div>
        </div>
      )}

      {/* Flush history (last 5) */}
      {history.length > 0 && (
        <div data-testid="bp-exec-diag-flush-history">
          <div className="text-[11px] font-medium text-gray-700 mb-1">
            Recent flushes{' '}
            <span className="text-gray-400 font-normal">
              · last {Math.min(history.length, 5)}
            </span>
          </div>
          <div className="space-y-1">
            {history.slice(-5).reverse().map((h, i) => (
              <div
                key={i}
                className="text-[10px] text-gray-600 flex items-center gap-2 bg-gray-50 px-2 py-1 rounded border border-gray-100"
              >
                <span className="text-gray-400">{h.checked_at}</span>
                <span>
                  abandoned{' '}
                  <strong>{h.stuck}/{h.max_workers}</strong> slots
                </span>
                <span className="text-gray-400">
                  oldest {fmtAge(h.oldest_active_age_sec || 0)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BPExecutorDiagnosticsPanel;
